import { postJwtLoginIdp } from '@framework/api-client';
import { message } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useAuth as useOpenIDConnectAuth } from 'react-oidc-context';
import { useDispatch } from 'react-redux';
import { AuthSource, authSourceToPath } from '../../AuthSource';
import { getNetworkErrorMessage } from '../../http/errors/getNetworkErrorMessage';
import { AuthActions } from '../../state/AuthSlice';
import { OpenIDConnectStrategyCode } from './OpenIDConnectConstants';

interface IUseOpenIDConnectLoginOptions {
  /** Nome dell'IdP per l'endpoint BE */
  idpName: string;
  source: AuthSource;
}

export function useOpenIDConnectLogin(options: IUseOpenIDConnectLoginOptions) {
  const { idpName, source } = options;
  const oidcContext = useOpenIDConnectAuth();
  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    activeNavigator,
    user,
    error,
  } = oidcContext;
  const dispatch = useDispatch();

  /**
   * Gestisce il login con la redirect alla pagina dello IAM che implementa
   * OpenID Connect
   */
  const handleLogin = useCallback(
    async (loginType: string) => {
      if (loginType === 'redirect') {
        try {
          await oidcContext.signinRedirect({
            // Costruiamo l'URL di redirect con il path corretto per la callback,
            // in base alla sorgente dell'autenticazione (FO, BO, ecc.) e all'IdP
            // scelto.
            redirect_uri:
              window.location.origin?.replace('http://', 'https://') +
              authSourceToPath(source) +
              '/login/' +
              idpName,
          });
        } catch (e) {
          console.error(`[Errore OIDC]`, e);
        }
      }
    },
    [idpName, oidcContext, source],
  );

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function handleOidcResponse() {
      if (isAuthenticated) {
        setLoading(true);

        try {
          console.log('[oidc] auth recognized');

          // Invio il token OIDC / OAuth2.0 al Backend per emettere il JWT con
          // il token di accesso.
          const result = await postJwtLoginIdp(idpName, {
            headers: {
              'X-Login-Provenienza': source,
              'X-OpenIDConnect-AccessToken': user?.access_token ?? '',
              'X-OpenIDConnect-IdToken': user?.id_token ?? '',
              'X-OpenIDConnect-RefreshToken': user?.refresh_token ?? '',
            },
          });
          dispatch(
            AuthActions.loginSucceed({
              accessToken: result.accessToken ?? '',
              refreshToken: result.refreshToken ?? '',
              strategy: OpenIDConnectStrategyCode,
            }),
          );
        } catch (e) {
          console.error(`[oidc] fetch token error`, e);
          message.error(getNetworkErrorMessage(e, `Si è verificato un errore durante il login, si prega di riprovare.`)); // prettier-ignore
        } finally {
          setLoading(false);
        }
      }
    }

    handleOidcResponse().catch(e => {
      console.error(`[Errore OIDC]`, e);
    });
  }, [dispatch, idpName, isAuthenticated, source, user]);

  return {
    isAuthenticated,
    isAuthLoading,
    activeNavigator,
    user,
    handleLogin,
    loading,
    error,
  };
}
